<template>
  <div class="date-time-range-input clearfix">
    <!-- From Date -->
    <div class="form-group row">
      <label :class="options.fullWidth ? 'col-sm-12' : 'col-sm-3 col-form-label'">
        Start {{ pickerOptions.label }}
        <span v-if="pickerOptions.required" class="required">*</span>
      </label>
      <div :class="options.fullWidth ? 'col-sm-12' : 'col-sm-9'">
        <div class="row">
          <div class="col-md-12">
            <div class="input-group">
              <div class="input-group-prepend">
                <span class="input-group-text">
                  <span class="fa fa-fw fa-calendar"></span>
                </span>
              </div>
              <input ref="start" class="form-control" type="text" @input="validate($event)"/>
            </div>
          </div>
        </div>
        <!-- Validation messages -->
        <div class="row">
          <div class="col-sm-12">
            <span class="invalid-feedback" v-show="!validStart">
              <span class="fa fa times"></span>
              Start {{ pickerOptions.label }} is not a valid date.
            </span>
          </div>
        </div>
      </div>
    </div>
    <!-- To Date -->
    <div class="form-group row">
      <label :class="options.fullWidth ? 'col-sm-12' : 'col-sm-3 col-form-label'">
        End {{ pickerOptions.label }}
        <span v-if="pickerOptions.required" class="required">*</span>
      </label>
      <div :class="options.fullWidth ? 'col-sm-12' : 'col-sm-9'">
        <div class="row">
          <div class="col-md-12">
            <div class="input-group">
              <div class="input-group-prepend">
                <span class="input-group-text">
                  <span class="fa fa-fw fa-calendar"></span>
                </span>
              </div>
              <input ref="end" class="form-control" type="text" @input="validate($event)"/>
            </div>
          </div>
        </div>
        <!-- Validation messages -->
        <div class="row">
          <div class="col-sm-12">
            <span class="invalid-feedback" v-show="!validEnd">
              <span class="fa fa times"></span>
              End {{ pickerOptions.label }} is not a valid date.
            </span>
          </div>
        </div>
        <div class="row">
          <div class="col-sm-12">
            <span class="invalid-feedback" v-show="!isValid">
              <span class="fa fa times"></span>
              The range you selected is invalid.
            </span>
            <span class="invalid-feedback" v-show="!validDuration">
              <span class="fa fa times"></span>
              A booking's duration can be only be from
              {{ pickerOptions.minimumMinutes }} minutes up to
              {{ pickerOptions.maximumDays }} days
            </span>
          </div>
        </div>
      </div>
    </div>
    <input type="hidden"
      :name="pickerOptions.name + '[start]'" :value="startDate"/>
    <input type="hidden"
      :name="pickerOptions.name + '[end]'" :value="endDate"/>
  </div>
</template>

<script>
import TimeInput from './TimeInput'
import moment from 'moment-timezone'

export default {
  name: 'DateRangeInput',
  components: {
    TimeInput
  },
  props: {
    value: {
      type: Object,
      default () {
        return {
          startDate: '',
          endDate: ''
        }
      }
    },
    options: {
      type: Object,
      default () {
        return {
          name: '',
          label: ''
        }
      }
    }
  },
  computed: {
    pickerOptions () {
      return {
        format: 'DD/MM/YYYY',
        minDate: null,
        maxDate: null,
        ...this.options
      }
    },
    isValid () {
      return this.validStart && this.validEnd &&
        this.validRange && this.validDuration
    },
    validRange () {
      if (this.startDate && this.endDate && this.pickerOptions.required) {
        return moment(this.endDate).isAfter(this.startDate)
      } else {
        return true
      }
    },
    validDuration () {
      if (this.pickerOptions.minimumMinutes && this.pickerOptions.maximumDays &&
        this.pickerOptions.required) {
        let m = moment(this.endDate)
        .diff(this.startDate, 'minutes', true)
        let d = moment(this.endDate)
        .diff(this.startDate, 'days', true)
        return m >= this.pickerOptions.minimumMinutes &&
          d <= this.pickerOptions.maximumDays
      } else {
        return true
      }
    }
  },
  data () {
    return {
      startPicker: null,
      endPicker: null,
      validStart: false,
      validEnd: false,
      startDate: null,
      endDate: null
    }
  },
  mounted () {
    // initialize date pickers
    this.startDate = moment(this.value.startDate).isValid()
      ? moment(this.value.startDate).toDate() : null
    this.endDate = moment(this.value.endDate).isValid()
      ? moment(this.value.endDate).toDate() : null
    this.startPicker = new Pikaday({
      field: this.$refs.start,
      format: this.pickerOptions.format,
      defaultDate: this.startDate,
      setDefaultDate: true,
      minDate: this.pickerOptions.minDate,
      maxDate: this.pickerOptions.maxDate,
      startRange: this.startDate,
      endRange: this.endDate,
      onSelect: (date) => {
        this.startPicker.setStartRange(date)
        this.endPicker.setStartRange(date)
        this.endPicker.setMinDate(date)
        this.validate()
      }
    })
    this.endPicker = new Pikaday({
      field: this.$refs.end,
      format: this.pickerOptions.format,
      defaultDate: this.endDate,
      setDefaultDate: true,
      minDate: this.pickerOptions.minDate,
      maxDate: this.pickerOptions.maxDate,
      startRange: this.startDate,
      endRange: this.endDate,
      onSelect: (date) => {
        this.startPicker.setEndRange(date)
        this.endPicker.setEndRange(date)
        this.validate()
      }
    })
    this.validate()
  },
  methods: {
    setDateRange (dateRange) {
      this.startPicker.setDate(moment(dateRange.startDate).toDate())
      this.endPicker.setDate(moment(dateRange.endDate).toDate())
      this.validate()
    },
    validate (event = false) {
      if (event && event.target.value === '') {
        if (event.target === this.$refs.start) {
          this.startPicker.setDate(null)
        }
        if (event.target === this.$refs.end) {
          this.endPicker.setDate(null)
        }
      }
      if (this.startPicker && this.endPicker) {
        let startRange = moment(this.startPicker.getDate())
        let endRange = moment(this.endPicker.getDate())
        this.validStart = startRange.isValid()
        this.validEnd = endRange.isValid()
        let value = {
          startDate: this.validStart ? startRange.format() : null,
          endDate: this.validEnd ? endRange.endOf('day').format() : null
        }
        this.startDate = value.startDate
        this.endDate = value.endDate
        this.$emit('input', value)
        if (!this.pickerOptions.required && !this.startDate && !this.endDate) {
          this.validStart = true
          this.validEnd = true
        }
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/inputs/DateRangeInput.vue